import React from 'react';
import { FiAlertTriangle, FiZap, FiImage, FiLoader } from 'react-icons/fi';
import { FeedbackData } from './ClosedBetaWizard';

interface StepReviewProps {
  feedbackData: FeedbackData;
  attachments: File[];
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting: boolean;
  error: string | null;
}

export const StepReview: React.FC<StepReviewProps> = ({
  feedbackData,
  attachments,
  onSubmit,
  onBack,
  isSubmitting,
  error
}) => {
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'P0': return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
      case 'P1': return 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200';
      case 'P2': return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200';
      case 'P3': return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high-impact': return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
      case 'helpful': return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
      case 'nice-to-have': return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
          {feedbackData.type === 'BUG' ? (
            <FiAlertTriangle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          ) : (
            <FiZap className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          )}
        </div>
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            {feedbackData.type === 'BUG' ? 'Bug Report' : 'Feature Suggestion'}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Please review your submission before sending
          </p>
        </div>
      </div>

      {/* Content Review */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-4">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
            Title
          </label>
          <p className="text-gray-900 dark:text-gray-100 font-medium">
            {feedbackData.title}
          </p>
        </div>

        {/* Bug-specific fields */}
        {feedbackData.type === 'BUG' && (
          <>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Category
                </label>
                <p className="text-gray-900 dark:text-gray-100">
                  {feedbackData.category}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Severity
                </label>
                <span className={`inline-block px-2 py-1 rounded-md text-sm font-medium ${getSeverityColor(feedbackData.severity)}`}>
                  {feedbackData.severity}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                Steps to Reproduce
              </label>
              <ol className="list-decimal list-inside space-y-1 text-gray-900 dark:text-gray-100">
                {feedbackData.steps.map((step, index) => (
                  <li key={index} className="text-sm">
                    {step}
                  </li>
                ))}
              </ol>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Expected Behavior
                </label>
                <p className="text-sm text-gray-900 dark:text-gray-100">
                  {feedbackData.expected}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Actual Behavior
                </label>
                <p className="text-sm text-gray-900 dark:text-gray-100">
                  {feedbackData.actual}
                </p>
              </div>
            </div>
          </>
        )}

        {/* Suggestion-specific fields */}
        {feedbackData.type === 'SUGGESTION' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                Suggestion/Idea
              </label>
              <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
                {feedbackData.suggestion}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                Expected Impact
              </label>
              <span className={`inline-block px-2 py-1 rounded-md text-sm font-medium ${getImpactColor(feedbackData.impact)}`}>
                {feedbackData.impact === 'nice-to-have' ? 'Nice to Have' : 
                 feedbackData.impact === 'helpful' ? 'Helpful' : 'High Impact'}
              </span>
            </div>
          </>
        )}

        {/* Additional Context */}
        {feedbackData.context && (
          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
              Additional Context
            </label>
            <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
              {feedbackData.context}
            </p>
          </div>
        )}

        {/* Attachments */}
        {attachments.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
              Attachments ({attachments.length})
            </label>
            <div className="space-y-2">
              {attachments.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-2 bg-white dark:bg-gray-700 rounded border"
                >
                  <FiImage className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-300">
            {error}
          </p>
        </div>
      )}

      {/* Environment Info */}
      <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 p-3 rounded">
        <p className="font-medium mb-1">Environment Information (automatically captured):</p>
        <p>URL: {window.location.href}</p>
        <p>Browser: {navigator.userAgent.split(' ').slice(-2).join(' ')}</p>
        <p>Viewport: {window.innerWidth} × {window.innerHeight}</p>
      </div>

      {/* Actions */}
      <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50"
        >
          Back
        </button>
        <button
          onClick={onSubmit}
          disabled={isSubmitting}
          className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
        >
          {isSubmitting && <FiLoader className="w-4 h-4 animate-spin" />}
          {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
        </button>
      </div>
    </div>
  );
}; 